import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';

import { LangPipe } from '@reforgium/presentia';

import { AccessibilityService, ColorScheme, FontFamily, FontSize, LetterSpacing } from './accessibility.service';
import { AccessibilitySpeechService } from './accessibility-speech.service';

@Component({
  selector: 'app-accessibility-panel',
  imports: [LangPipe],
  template: `
    <button
      #trigger
      type="button"
      class="accessibility-trigger"
      [attr.aria-label]="'common.accessibility.open' | lang"
      aria-haspopup="dialog"
      aria-controls="accessibility-dialog"
      [attr.aria-expanded]="isOpen()"
      (click)="open()"
    >
      <span class="trigger-icon" aria-hidden="true">Аа</span>
      <span class="trigger-label">{{ 'common.accessibility.open' | lang }}</span>
    </button>

    <dialog
      #panel
      id="accessibility-dialog"
      class="accessibility-dialog"
      aria-labelledby="accessibility-title"
      (close)="onClose()"
      (pointerdown)="onBackdropPointerDown($event)"
    >
      <div class="dialog-content">
        <header class="dialog-header">
          <div>
            <p class="eyebrow">{{ 'common.accessibility.displaySettings' | lang }}</p>
            <h2 id="accessibility-title">{{ 'common.accessibility.title' | lang }}</h2>
          </div>
          <button
            #closeButton
            type="button"
            class="close-button"
            [attr.aria-label]="'common.accessibility.close' | lang"
            (click)="close()"
          >
            ×
          </button>
        </header>

        <div class="settings-list">
          <fieldset>
            <legend>{{ 'common.accessibility.soundAssistant' | lang }}</legend>
            <div class="options">
              <button
                type="button"
                [attr.aria-pressed]="accessibility.settings().soundAssistant"
                (click)="setSoundAssistant(true)"
              >
                {{ 'common.accessibility.on' | lang }}
              </button>
              <button
                type="button"
                [attr.aria-pressed]="!accessibility.settings().soundAssistant"
                (click)="setSoundAssistant(false)"
              >
                {{ 'common.accessibility.off' | lang }}
              </button>
            </div>
            <div class="speech-controls">
              <button
                type="button"
                [disabled]="!accessibility.settings().soundAssistant || !speech.supported"
                [attr.aria-disabled]="speech.state() === 'speaking'"
                (click)="play()"
              >
                <span class="speech-icon" aria-hidden="true">▶</span>
                <span class="speech-label">{{ 'common.accessibility.play' | lang }}</span>
              </button>
              <button type="button" [attr.aria-disabled]="speech.state() !== 'speaking'" (click)="stop()">
                <span class="speech-icon" aria-hidden="true">■</span>
                <span class="speech-label">{{ 'common.accessibility.stop' | lang }}</span>
              </button>
              <button
                type="button"
                [disabled]="!accessibility.settings().soundAssistant || !speech.supported || !speech.hasPlayed()"
                (click)="speech.repeat()"
              >
                <span class="speech-icon" aria-hidden="true">↻</span>
                <span class="speech-label">{{ 'common.accessibility.repeat' | lang }}</span>
              </button>
            </div>
            <p class="setting-note" role="status" aria-live="polite">
              @if (!speech.supported) {
                {{ 'common.accessibility.soundUnsupported' | lang }}
              } @else if (speech.state() === 'speaking') {
                {{ 'common.accessibility.soundReading' | lang }}
              } @else if (speech.state() === 'empty') {
                {{ 'common.accessibility.soundEmpty' | lang }}
              } @else if (speech.state() === 'error') {
                {{ 'common.accessibility.soundError' | lang }}
              } @else {
                {{ 'common.accessibility.soundNote' | lang }}
              }
            </p>
          </fieldset>

          <fieldset>
            <legend>{{ 'common.accessibility.fontSize' | lang }}</legend>
            <div class="options">
              @for (size of fontSizes; track size) {
                <button
                  type="button"
                  [attr.aria-pressed]="accessibility.settings().fontSize === size"
                  (click)="accessibility.set('fontSize', size)"
                >
                  {{ 'common.accessibility.size.' + size | lang }}
                </button>
              }
            </div>
          </fieldset>

          <fieldset>
            <legend>{{ 'common.accessibility.fontFamily' | lang }}</legend>
            <div class="options">
              @for (family of fontFamilies; track family) {
                <button
                  type="button"
                  [attr.aria-pressed]="accessibility.settings().fontFamily === family"
                  (click)="accessibility.set('fontFamily', family)"
                >
                  {{ 'common.accessibility.family.' + family | lang }}
                </button>
              }
            </div>
          </fieldset>

          <fieldset>
            <legend>{{ 'common.accessibility.letterSpacing' | lang }}</legend>
            <div class="options">
              @for (spacing of letterSpacings; track spacing) {
                <button
                  type="button"
                  [attr.aria-pressed]="accessibility.settings().letterSpacing === spacing"
                  (click)="accessibility.set('letterSpacing', spacing)"
                >
                  {{ 'common.accessibility.spacing.' + spacing | lang }}
                </button>
              }
            </div>
          </fieldset>

          <fieldset>
            <legend>{{ 'common.accessibility.colorScheme' | lang }}</legend>
            <div class="options">
              @for (scheme of colorSchemes; track scheme) {
                <button
                  type="button"
                  [attr.aria-pressed]="accessibility.settings().colorScheme === scheme"
                  (click)="accessibility.set('colorScheme', scheme)"
                >
                  {{ 'common.accessibility.color.' + scheme | lang }}
                </button>
              }
            </div>
          </fieldset>

          <fieldset>
            <legend>{{ 'common.accessibility.images' | lang }}</legend>
            <div class="options">
              <button
                type="button"
                [attr.aria-pressed]="accessibility.settings().images"
                (click)="accessibility.set('images', true)"
              >
                {{ 'common.accessibility.show' | lang }}
              </button>
              <button
                type="button"
                [attr.aria-pressed]="!accessibility.settings().images"
                (click)="accessibility.set('images', false)"
              >
                {{ 'common.accessibility.hide' | lang }}
              </button>
            </div>
          </fieldset>
        </div>

        <footer class="dialog-footer">
          <button type="button" class="reset-button" (click)="reset()">
            {{ 'common.accessibility.reset' | lang }}
          </button>
        </footer>
      </div>
    </dialog>
  `,
  styles: `
    .accessibility-trigger {
      position: fixed;
      z-index: 1100;
      bottom: calc(12px + env(safe-area-inset-bottom));
      left: max(16px, env(safe-area-inset-left));
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      min-height: 44px;
      padding: 0.4rem 0.9rem 0.4rem 0.4rem;
      border: 1px solid #92bcdf;
      border-radius: 999px;
      background: #17547f;
      color: #fff;
      font-size: 0.85rem;
      font-weight: 600;
      box-shadow: 0 6px 22px rgb(17 40 75 / 24%);
      cursor: pointer;
    }
    .accessibility-trigger:hover {
      background: #0d4269;
    }
    .accessibility-trigger:focus-visible,
    .accessibility-dialog button:focus-visible {
      outline: 3px solid #f5b82e;
      outline-offset: 3px;
    }
    .trigger-icon {
      display: grid;
      place-items: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #fff;
      color: #17547f;
      font-weight: 800;
    }
    .accessibility-dialog {
      position: fixed;
      inset: auto auto calc(72px + env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
      box-sizing: border-box;
      width: min(440px, calc(100vw - 32px));
      max-width: none;
      max-height: calc(100dvh - 96px - env(safe-area-inset-bottom));
      margin: 0;
      padding: 0;
      overflow: auto;
      border: 1px solid var(--border-color);
      border-radius: 18px;
      background: var(--surface-neutral);
      color: var(--text-primary);
      box-shadow: 0 24px 70px rgb(0 0 0 / 25%);
    }
    .accessibility-dialog::backdrop {
      background: rgb(9 18 36 / 55%);
    }
    .dialog-content {
      padding: 0;
    }
    .dialog-header {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 1.25rem;
      background: var(--surface-neutral);
      display: block;
    }
    .eyebrow {
      margin: 0 0 0.5rem;
      padding-right: 52px;
      color: var(--text-secondary);
      font-size: 0.75rem;
    }
    h2 {
      margin: 0;
      overflow-wrap: anywhere;
      font-size: 1.25rem;
      line-height: 1.3;
    }
    .close-button {
      position: absolute;
      top: 1.25rem;
      right: 1.25rem;
      width: 44px;
      height: 44px;
      padding: 0;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      background: var(--surface-background);
      color: var(--text-primary);
      font-size: 1.5rem;
      cursor: pointer;
    }
    .settings-list {
      display: grid;
      gap: 0.85rem;
      margin-top: 0;
      padding-inline: 1.25rem;
    }
    fieldset {
      min-width: 0;
      margin: 0;
      padding: 0;
      border: 0;
    }
    legend {
      margin-bottom: 0.4rem;
      font-size: 0.86rem;
      font-weight: 700;
    }
    .options {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
    }
    .options button,
    .reset-button {
      min-height: 44px;
      max-width: 100%;
      white-space: normal;
      overflow-wrap: anywhere;
      padding: 0.45rem 0.7rem;
      border: 1px solid var(--border-color);
      border-radius: 9px;
      background: var(--surface-background);
      color: var(--text-primary);
      font-size: 0.8rem;
      cursor: pointer;
    }
    .options button:not(:disabled):hover,
    .reset-button:hover {
      border-color: var(--primary-color);
    }
    .options button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .speech-controls {
      display: grid;
      gap: 0.45rem;
      box-sizing: border-box;
      width: min(100%, 18rem);
      margin-top: 0.6rem;
      padding: 0.7rem;
      border-radius: 12px;
      background: #10283e;
      box-shadow: 0 8px 22px rgb(16 40 62 / 20%);
    }
    .speech-controls button {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      width: 100%;
      min-height: 44px;
      padding: 0.55rem 0.7rem;
      border: 1px solid transparent;
      border-radius: 8px;
      background: #fff;
      color: #10283e;
      font-size: 0.83rem;
      font-weight: 700;
      text-align: left;
      cursor: pointer;
    }
    .speech-controls button:not(:disabled):not([aria-disabled='true']):hover {
      background: #e5f2fb;
    }
    .speech-controls button:disabled,
    .speech-controls button[aria-disabled='true'] {
      background: #8d9eab;
      color: #10283e;
      cursor: not-allowed;
    }
    .speech-label {
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .speech-icon {
      display: inline-grid;
      flex: 0 0 1rem;
      place-items: center;
      font-size: 1rem;
      line-height: 1;
    }
    .options button[aria-pressed='true'] {
      border-color: var(--a11y-accent, #17547f);
      background: var(--a11y-accent, #17547f);
      color: var(--a11y-on-accent, #fff);
    }
    .setting-note {
      margin: 0.4rem 0 0;
      color: var(--text-secondary);
      font-size: 0.72rem;
    }
    .dialog-footer {
      margin: 1.2rem 1.25rem 0;
      padding-bottom: 1.25rem;
      padding-top: 0.9rem;
      border-top: 1px solid var(--border-color);
    }
    .reset-button {
      width: 100%;
      font-weight: 700;
    }
    @media (max-width: 768px) {
      .accessibility-dialog {
        inset: auto 12px calc(72px + env(safe-area-inset-bottom));
        width: auto;
        max-height: calc(100dvh - 90px - env(safe-area-inset-bottom));
      }
      .accessibility-trigger {
        box-sizing: border-box;
        width: 44px;
        height: 44px;
        min-height: 44px;
        padding: 5px;
      }
      .trigger-label {
        display: none;
      }
    }
  `,
})
export class AccessibilityPanelComponent {
  protected readonly accessibility = inject(AccessibilityService);
  protected readonly speech = inject(AccessibilitySpeechService);
  protected readonly panel = viewChild.required<ElementRef<HTMLDialogElement>>('panel');
  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly closeButton = viewChild.required<ElementRef<HTMLButtonElement>>('closeButton');

  protected readonly isOpen = signal(false);
  protected readonly fontSizes: readonly FontSize[] = ['normal', 'large', 'largest'];
  protected readonly fontFamilies: readonly FontFamily[] = ['default', 'sans', 'serif'];
  protected readonly letterSpacings: readonly LetterSpacing[] = ['normal', 'wide', 'wider'];
  protected readonly colorSchemes: readonly ColorScheme[] = ['default', 'contrast', 'grayscale', 'blue'];

  protected play(): void {
    if (this.speech.state() !== 'speaking') this.speech.play();
  }

  protected stop(): void {
    if (this.speech.state() === 'speaking') this.speech.stop();
  }

  protected setSoundAssistant(enabled: boolean): void {
    if (!enabled) this.speech.stop(true);
    this.accessibility.set('soundAssistant', enabled);
  }

  protected reset(): void {
    this.speech.stop(true);
    this.accessibility.reset();
  }

  protected open(): void {
    this.panel().nativeElement.showModal();
    this.isOpen.set(true);
    this.closeButton().nativeElement.focus();
  }

  protected close(): void {
    this.panel().nativeElement.close();
  }

  protected onClose(): void {
    this.isOpen.set(false);
    this.trigger().nativeElement.focus();
  }

  protected onBackdropPointerDown(event: PointerEvent): void {
    if (event.target === this.panel().nativeElement) this.close();
  }
}
